/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: prop-types library.
import _ from 'underscore' // Npm: underscore.js library.
import { useLazyQuery, useQuery } from '@apollo/client' // Npm: Apollo client.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useHistory } from 'react-router-dom' // Npm: React router dom.
import { HiArrowUturnDown, HiArrowPath } from 'react-icons/hi2' // Npm: react-icons library.
import { DataGrid, GridFooter } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Flex,
  Text,
  useBreakpointValue
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import DateRangePicker from 'components/DateRangePicker'
import TableSpinner from 'components/TableSpinner'
import { MemoizedSelect } from 'components/MemoizedInput'


/*
 * GRAPHS
 */
import VendorMarginReportQuery from './__query__/index.vendor.marginReport.query'
import VendorAccountReadQuery from './__query__/index.vendorAccount.read.query'


/*
 * OBJECTS
 */
const Index = ({ passOn, PassOnUpdate }) => {
  const _historyRef = useHistory()

  // Const assignment.
  const _skipDifference = 10

  // Hook assignment.
  const [dateRange, setDateRange] = React.useState({
    'selection': {
      'startDate': Object.DateRangeProvider()?.startDate,
      'endDate': Object.DateRangeProvider()?.endDate,
      'key': 'selection'
    }
  })
  const [vendorAccountId, setVendorAccountId] = React.useState(void 0)
  const _vendorAccountIdRef = React.useRef(void 0)
  const [smsRead, setSmsRead] = React.useState([])
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': _skipDifference,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [refreshTrigger, setRefreshTrigger] = React.useState(0)

  const [QueryVendorMarginReport, QueryVendorMarginReportResponse] = useLazyQuery(VendorMarginReportQuery, {
    'variables': {
      'vendorId': passOn?.vendorId,
      'startDate': dateRange?.selection.startDate,
      'endDate': dateRange?.selection.endDate
    },
    'fetchPolicy': Object.React.App.fetchPolicy,
  })
  const _QueryVendorAccountRead = useQuery(VendorAccountReadQuery, {
    'variables': { 'vendorId': passOn?.vendorId },
    'fetchPolicy': Object.React.App.fetchPolicy,
  })
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({ 'base': 'false', 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true })


  // Handle country click
  const _handleCountryClick = (row) => {
    const _vendorName = _vendorAccountIdRef.current?.split('(')?.[0]?.trim()

    PassOnUpdate({
      'vendorId': passOn?.vendorId,
      'currency': passOn?.currency,
      'countryName': row.countryName,
      'vendorName': _vendorName,
      '_navigatedToMessage': true
    })
    _historyRef.push('/reports/message')
  }

  // Event handler
  React.useEffect(() => {
    // Async handler.
    const _Async = async () => {
      // Refetch data on date range change.
      const id = vendorAccountId?.split('(')[1]?.split(')')?.[0]
      const { data, error } = await QueryVendorMarginReport({ 'variables': { 'vendorId': passOn?.vendorId, 'startDate': dateRange.selection.startDate, 'endDate': dateRange.selection.endDate }, 'fetchPolicy': Object.React.App.fetchPolicy })

      // If getting data caught exception then report failure.
      if (error instanceof Error) throw error

      // Const assignment.
      const _report = _.compact(Object.entries(data?.VendorMarginReport?._report ?? {}))

      // If data is not empty then update it.
      if (0 < _report?.length) {
        // Const assignment.
        const _totalsumup = {}

        // Merge all by country names.
        for (const [__, value] of _.compact(Object.values(_report))) {
          // Break by countryName.
          for (const [key, value2] of Object.entries(value)) {
            // If country name is not in the list then add it.
            if (!_totalsumup[key]) _totalsumup[key] = { 'totalsmscount': 0, 'totaldelivered': 0, 'totalfailed': 0, 'dlrPercentage': 0, 'revenue': 0, 'cost': 0, 'margin': 0 }

            if (__ == id || _.isEmpty(id)) {
              // Update the values.
              _totalsumup[key].totalsmscount += (value2?.totalsmscount ?? 0)
              _totalsumup[key].totaldelivered += (value2?.totaldelivered ?? 0)
              _totalsumup[key].totalfailed += (value2?.totalfailed ?? 0)
              _totalsumup[key].dlrPercentage = _totalsumup[key].totalsmscount > 0
                ? ((_totalsumup[key].totaldelivered / _totalsumup[key].totalsmscount) * 100).toFixed(2)
                : 0;
              _totalsumup[key].revenue += (value2?.revenue ?? 0)
              _totalsumup[key].cost += (value2?.cost ?? 0)
              _totalsumup[key].margin += (value2?.margin ?? 0)
            }
          }
        }

        // Update the state.
        setSmsRead(Object.entries(_totalsumup))
      }
    }; _Async().catch(e => e).then(j => j)
  }, [dateRange, passOn?.vendorId, vendorAccountId, refreshTrigger])

  // Total row values .
  const _totalRow = React.useMemo(() => {
    const _marginTotals = QueryVendorMarginReportResponse?.data?.VendorMarginReport?.marginTotals

    return {
      'revenue': parseFloat(_marginTotals?.totalRevenue ?? 0).toFixed(2),
      'cost': parseFloat(_marginTotals?.totalCost ?? 0).toFixed(2),
      'margin': parseFloat(_marginTotals?.totalMargin ?? 0).toFixed(2)
    }
  }, [QueryVendorMarginReportResponse?.data])

  // Data assignment.
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S.No.',
      'align': 'center',
      'headerAlign': 'center',
      'width': 80,
      'sortable': true,
      'filterable': false
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'headerAlign': 'center',
      'hide': true
    },
    {
      'field': 'countryName',
      'headerName': 'Country Name',
      'description': 'Name of the country.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 200,
      'renderCell': ({ row }) => (
        <a
          onClick={() => _handleCountryClick(row)}
          style={{
            'color': '#6366F1',
            'cursor': 'pointer',
            'textDecoration': 'underline'
          }}>
          {row.countryName}
        </a>
      )
    },
    {
      'field': 'totalCount',
      'headerName': 'Total Count',
      'description': 'Total SMS count.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 90

    },
    {
      'field': 'totalDelivered',
      'headerName': 'Total Delivered',
      'description': 'Total delivered SMS.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 100


    },
    {
      'field': 'totalFailed',
      'headerName': 'Total Failed',
      'description': 'Total failed SMS.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 100


    },
    {
      'field': 'dlrPercentage',
      'headerName': 'Dlr%',
      'description': 'Delivery rate percentage.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 80


    },
    {
      'field': 'revenue',
      'headerName': `Revenue (${passOn?.currency || ''})`,
      'description': 'Total revenue.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 100


    },
    {
      'field': 'cost',
      'headerName': `Cost (${passOn?.currency || ''})`,
      'description': 'Total cost.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 100


    },
    {
      'field': 'margin',
      'headerName': `Margin (${passOn?.currency || ''})`,
      'description': 'Total margin.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150


    }
  ], [passOn?.currency])

  const rows = React.useMemo(() => smsRead?.map((item, index) => ({
    'id': `${item?.[0]}-${index}` || String.random(9),
    'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
    'countryName': item?.[0] ?? '-',
    'totalCount': item?.[1]?.totalsmscount ?? '-',
    'totalDelivered': item?.[1]?.totaldelivered ?? '-',
    'totalFailed': item?.[1]?.totalfailed ?? '-',
    'dlrPercentage': item?.[1]?.dlrPercentage != null ? `${item[1].dlrPercentage}%` : '-',
    'revenue': parseFloat(item?.[1]?.revenue ?? 0)?.toFixed?.(2) ?? '-',
    'cost': parseFloat(item?.[1]?.cost ?? 0)?.toFixed?.(2) ?? '-',
    'margin': parseFloat(item?.[1]?.margin ?? 0)?.toFixed?.(2) ?? '-'
  })) || [], [smsRead, paginationModel.page])

  // Return component.
  return (
    <>
      <Flex className='reportsCustomer base'>
        <Flex
          display='flex'
          flex={1}
          flexDirection='column'
          gap={_isCurrentViewMobile ? '12px' : '22px'}
          bg='white'
          borderRadius='20px'
          p={_isCurrentViewMobile ? '12px' : '22px'}>
          <Flex pb='0px' justifyContent='space-between' alignItems={_isCurrentViewMobile ? 'stretch' : 'center'} flexDir={_isCurrentViewMobile ? 'column' : 'row'} gap={_isCurrentViewMobile ? '16px' : '0px'}>
            <Flex flex={_isCurrentViewMobile ? 1 : 0.6} gap='12px' flexDir='column'>
              <Text as='h3' fontSize={17} fontWeight={500} display='flex' alignItems='center' color='brand.500' gap='6px'>
                <HiArrowUturnDown />{_isCurrentViewMobile ? 'Filter Rows' : 'Filter Rows based on Date'}.
              </Text>
              <DateRangePicker
                startDate={dateRange?.selection?.startDate}
                endDate={dateRange?.selection?.endDate}
                w='100%'
                fontSize='13px'
                height='max-content'
                gap='6px'
                setSelectDate={setDateRange}
              />
            </Flex>
            <Flex flex={_isCurrentViewMobile ? 1 : 0.4} justify={_isCurrentViewMobile ? 'flex-start' : 'end'}>
              <Flex
                gap='12px'
                flexDir={{ base: 'column', md: 'row' }}
                flexWrap='wrap'
                w={{ base: '100%', md: 'auto' }}
              >
                <MemoizedSelect
                  w={{ base: '100%', md: '240px' }}
                  name='vendorId'
                  placeholder='Select Vendor Account'
                  bg='purple.50'
                  color='purple'
                  data={vendorAccountId}
                  options={(_QueryVendorAccountRead?.data?.VendorAccountRead ?? [])?.map(j => j.displayName ? `${j.displayName} (${j.id})` : void 0)}

                  onChange={e => {
                    setVendorAccountId(e.target?.value)
                    _vendorAccountIdRef.current = e.target?.value
                  }}
                />
                <Button
                  borderRadius='12px'
                  fontWeight='400'
                  maxW='250px'
                  w={{ base: '100%', md: 'auto' }}
                  bg='purple.50'
                  color='purple'
                  _hover={{ bg: 'purple.800', color: 'white' }}
                  onClick={() => {
                    _QueryVendorAccountRead.refetch({
                      'vendorId': passOn?.vendorId
                    })
                    setRefreshTrigger(prev => prev + 1)
                  }}
                  isDisabled={_QueryVendorAccountRead?.loading || QueryVendorMarginReportResponse?.loading}
                  leftIcon={<HiArrowPath />}
                >
                  Refresh
                </Button>
              </Flex>
            </Flex>
          </Flex>
          <Flex
            position="sticky"
            top="0"
            width="100%"
            height="calc(100vh - 260px)"
            minHeight="400px"
            flexDirection="column"
            sx={{
              '& .MuiDataGrid-root': {
                border: '1px solid #E2E8F0',
                borderRadius: '12px',
                height: '100%',
              },
              '& .MuiDataGrid-main': {
                overflowY: 'auto',
              }
            }}
          >
            <DataGrid
              key={`${paginationModel.page}-${paginationModel.pageSize}`}
              rows={rows}
              columns={columns}
              rowHeight={58}
              paginationMode='client'
              disableColumnFilter
              disableColumnMenu
              // rowCount={smsRead?.length || 0}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[
                10,
                20,
                50,
                100
              ]}
              slots={{
                'loadingOverlay': () => <TableSpinner isLoading={true} isEmpty={false} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
                'noResultsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
                'noRowsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
                'footer': () => (
                  <>

                    <Flex
                      w='100%'
                      borderBottom='1px solid'
                      borderLeft='1px solid'
                      borderRight='1px solid'
                      borderColor='#D8E3FC'
                      bg='white'
                      px='16px'
                      height='58px'
                      alignItems='center'
                      overflowX='auto'>
                      <Flex w='120px' justify='center'>
                        <Text fontWeight={700} color='brand.500' fontSize='14px'></Text>
                      </Flex>
                      <Flex flex={1} justify='center'>
                        <Text fontWeight={700} color='brand.500' fontSize='14px'>Total</Text>
                      </Flex>
                      <Flex flex={1} justify='center'>
                        <Text fontWeight={700} color='brand.500' fontSize='14px'></Text>
                      </Flex>
                      <Flex flex={1} justify='center'>
                        <Text fontWeight={700} color='brand.500' fontSize='14px'></Text>
                      </Flex>
                      <Flex flex={1} justify='center'>
                        <Text fontWeight={700} color='brand.500' fontSize='14px'></Text>
                      </Flex>
                      <Flex flex={1} justify='center' gap='8px'>
                        <Text fontWeight={600} fontSize='14px'>Rev:</Text>
                        <Text fontWeight={600} fontSize='14px'>{_totalRow.revenue}</Text>
                      </Flex>
                      <Flex flex={1} justify='center' gap='8px'>
                        <Text fontWeight={600} fontSize='14px'>cost:</Text>
                        <Text fontWeight={600} fontSize='14px'>{_totalRow.cost}</Text>
                      </Flex>
                      <Flex flex={1} justify='center' gap='8px'>
                        <Text fontWeight={600} fontSize='14px'>Mgn:</Text>
                        <Text fontWeight={600} fontSize='14px'>{_totalRow.margin}</Text>
                      </Flex>
                    </Flex>

                    <GridFooter />
                  </>
                )
              }}
              disableRowSelectionOnClick
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
              loading={QueryVendorMarginReportResponse?.loading || smsRead.length === 0}
            />
          </Flex>
        </Flex>
      </Flex>
    </>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'passOn': PropTypes.object,
  'PassOnUpdate': PropTypes.func
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })
const _MapDispatchToProps = __dispatch => ({ 'PassOnUpdate': u => __dispatch({ 'type': 'PASS_ON_UPDATE', 'PassOn': u }) })


/*
 * EXPORT
 */
export default connect(_MapStateToProps, _MapDispatchToProps)(Index)
